import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Button from '../common/Button';

const SettingsModal = ({ isOpen, onClose }) => {
  const { enableStockCheckBrandDeletion, setEnableStockCheckBrandDeletion } = useAuth();
  const toast = useToast();
  
  // Local state to hold the toggle value until user clicks "Save"
  const [tempEnableDeletion, setTempEnableDeletion] = useState(enableStockCheckBrandDeletion);
  const [isSaving, setIsSaving] = useState(false);

  // Sync temp state with context state when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempEnableDeletion(enableStockCheckBrandDeletion);
    }
  }, [isOpen, enableStockCheckBrandDeletion]);

  // Handle Escape key to close the modal
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleSave = () => {
    setIsSaving(true);
    try {
      setEnableStockCheckBrandDeletion(tempEnableDeletion);
      toast.success('Settings saved successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4 py-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-modal-title"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white dark:bg-slate-800 shadow-2xl max-h-dvh-90 flex flex-col border border-transparent dark:border-slate-700 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="p-6 pb-0 mb-6 flex items-center justify-between shrink-0">
          <div>
            <h2 id="settings-modal-title" className="text-2xl font-bold text-slate-800 dark:text-slate-100">
              Settings
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Manage your application preferences
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600 flex items-center justify-center transition-colors"
            aria-label="Close settings modal"
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 pb-6 overflow-y-auto space-y-6">
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-700/30 border border-slate-100 dark:border-slate-700 hover:bg-slate-100/50 dark:hover:bg-slate-700/40 transition-colors">
            <div className="flex flex-col gap-1 pr-4">
              <span className="text-sm font-bold text-slate-800 dark:text-slate-100">
                Enable Stock Check Brand Deletion
              </span>
              <span className="text-xs text-slate-400 dark:text-slate-500">
                Allows removing brands from entry templates and deleting records.
              </span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 select-none">
              <input
                type="checkbox"
                checked={tempEnableDeletion}
                onChange={(e) => setTempEnableDeletion(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 dark:bg-slate-600 rounded-full peer peer-focus:ring-2 peer-focus:ring-primary/20 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary peer-checked:to-accent shadow-inner"></div>
            </label>
          </div>

          {/* Modal Actions */}
          <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-700">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              loading={isSaving}
              onClick={handleSave}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
